'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard,
  Building2,
  CalendarDays,
  BookOpen,
  Star,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  TrendingUp,
  Users,
} from 'lucide-react';

const navGroups = [
  {
    label: 'Overview',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', href: '/admin-dashboard', badge: null },
      { icon: TrendingUp, label: 'Revenue', href: '/admin-dashboard', badge: null },
    ],
  },
  {
    label: 'Property',
    items: [
      { icon: Building2, label: 'Apartments', href: '/admin-dashboard', badge: '4' },
      { icon: CalendarDays, label: 'Availability', href: '/admin-dashboard', badge: null },
      { icon: BookOpen, label: 'Bookings', href: '/admin-dashboard', badge: '3' },
    ],
  },
  {
    label: 'Guests',
    items: [
      { icon: Users, label: 'Guests', href: '/admin-dashboard', badge: null },
      { icon: Star, label: 'Reviews', href: '/admin-dashboard', badge: '2' },
    ],
  },
  {
    label: 'System',
    items: [
      { icon: Settings, label: 'Settings', href: '/admin-dashboard', badge: null },
    ],
  },
];

export default function AdminSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  return (
    <aside
      className={`
        relative flex flex-col bg-white border-r border-border transition-all duration-300 ease-in-out shrink-0
        ${collapsed ? 'w-16' : 'w-60'}
      `}
      style={{ minHeight: '100vh' }}
    >
      {/* Logo */}
      <div className={`h-16 flex items-center border-b border-border px-3 ${collapsed ? 'justify-center' : 'gap-2 px-4'}`}>
        {collapsed ? (
          <AppLogo size={32} />
        ) : (
          <>
            <AppLogo size={32} />
            <span className="font-extrabold text-base text-foreground tracking-tight whitespace-nowrap">
              Vacay<span className="text-primary">Rent</span>
            </span>
          </>
        )}
      </div>
      {/* Collapse Toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 z-10 w-6 h-6 bg-white border border-border rounded-full flex items-center justify-center shadow-sm hover:bg-muted transition-colors"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
      {/* Nav Groups */}
      <nav className="flex-1 overflow-y-auto py-4 space-y-6 scrollbar-hide">
        {navGroups?.map((group) => (
          <div key={`group-${group?.label}`}>
            {!collapsed && (
              <p className="px-4 mb-1 text-[10px] font-700 uppercase tracking-widest text-muted-foreground">
                {group?.label}
              </p>
            )}
            <ul className="space-y-0.5 px-2">
              {group?.items?.map((item) => {
                const isActive = pathname === item?.href;
                return (
                  <li key={`nav-${item?.label}`}>
                    <Link
                      href={item?.href}
                      className={`
                        flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-500 transition-all duration-150 group relative
                        ${isActive
                          ? 'bg-primary/10 text-primary font-600' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }
                        ${collapsed ? 'justify-center' : ''}
                      `}
                      title={collapsed ? item?.label : undefined}
                    >
                      <item.icon size={18} className="shrink-0" />
                      {!collapsed && (
                        <>
                          <span className="flex-1 truncate">{item?.label}</span>
                          {item?.badge && (
                            <span className="ml-auto bg-primary text-primary-foreground text-[10px] font-700 px-1.5 py-0.5 rounded-full leading-none">
                              {item?.badge}
                            </span>
                          )}
                        </>
                      )}
                      {collapsed && item?.badge && (
                        <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      {/* User Profile */}
      <div className={`border-t border-border p-3 ${collapsed ? 'flex justify-center' : ''}`}>
        {collapsed ? (
          <button className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-700 text-sm">
            M
          </button>
        ) : (
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-700 text-sm shrink-0">
              M
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-600 text-foreground truncate">Maria Santos</p>
              <p className="text-xs text-muted-foreground truncate">Property Owner</p>
            </div>
            <button className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="Sign out">
              <LogOut size={15} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}